import React, { useEffect, useState, useRef, useMemo } from "react";
import * as axiosInstance from "../utils/axiosinstace";
import { getRole } from "../utils/common";
import { Role } from "../utils/role";
import { Multiselect } from "multiselect-react-dropdown";
import moment from "moment";
import { PieChartOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';

const MAX_BATCHES = 10;

const ComparisonReport = ({ title, renderChart, maxBatches = MAX_BATCHES, fetchData }) => {
  const { t } = useTranslation(['analytics', 'common']);
  const [role] = useState(getRole());
  const [farmOptions, setFarmOptions] = useState([]);
  const [selectedFarmers, setSelectedFarmers] = useState([]);
  const [selectedFarms, setSelectedFarms] = useState([]);
  const [filterMode, setFilterMode] = useState("");
  const [batchData, setBatchData] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [timeFilter, setTimeFilter] = useState("");
  const [preSelectedBatches, setPreSelectedBatches] = useState([]);
  const [truncatedCount, setTruncatedCount] = useState(0);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const dropdownRef = useRef(null);

  const farmKey = useMemo(
    () => selectedFarms.map((f) => f.farmID).sort().join(","),
    [selectedFarms]
  );
  const multiFarm = selectedFarms.length > 1;

  const farmerOptions = useMemo(() => {
    const seen = new Map();
    farmOptions.forEach((f) => {
      if (f.farmowner && !seen.has(f.farmowner)) {
        seen.set(f.farmowner, { id: f.farmowner, name: f.farmerName || f.farmowner });
      }
    });
    return Array.from(seen.values());
  }, [farmOptions]);

  const farmerKey = useMemo(
    () => selectedFarmers.map((f) => f.id).sort().join(","),
    [selectedFarmers]
  );

  const visibleFarmOptions = useMemo(() => {
    if (selectedFarmers.length === 0) return farmOptions;
    const owners = new Set(selectedFarmers.map((f) => f.id));
    return farmOptions.filter((f) => owners.has(f.farmowner));
  }, [farmOptions, selectedFarmers]);

  useEffect(() => {
    const getFarms = async () => {
      try {
        const response = await axiosInstance.getComparisonFarms();
        const options = (response.list || []).map((item) => ({
          id: item.farmID,
          name: item.farmName,
          farmID: item.farmID,
          farmName: item.farmName,
          farmowner: item.farmowner,
          farmerName: item.farmerName,
        }));
        setFarmOptions(options);
      } catch (err) {
        console.log("Error fetching farms:", err.message);
      }
    };
    getFarms();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBatchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch closed batches whenever the farm selection changes in batch mode
  useEffect(() => {
    const fetchBatches = async () => {
      setIsLoadingBatches(true);
      try {
        const response = await axiosInstance.getListofBatches(farmKey, "closed");
        setBatchData(response.list || []);
      } catch (err) {
        console.log("Error fetching batches:", err);
        setBatchData([]);
      } finally {
        setIsLoadingBatches(false);
      }
    };

    if (filterMode === "batch" && farmKey) {
      fetchBatches();
    } else {
      setBatchData([]);
    }
  }, [farmKey, filterMode]);

  const resetResults = () => {
    setChartData([]);
    setShowChart(false);
  };

  const onFarmSelect = (selectedList) => {
    setSelectedFarms(selectedList);
    setSelectedBatches([]);
    setPreSelectedBatches([]);
    setTruncatedCount(0);
    setTimeFilter("");
    resetResults();
  };

  const onFarmerSelect = (selectedList) => {
    setSelectedFarmers(selectedList);
    // No farmer filter means all farms stay visible — keep selections intact
    if (selectedList.length === 0) return;
    const owners = new Set(selectedList.map((f) => f.id));
    const pruned = selectedFarms.filter((f) => owners.has(f.farmowner));
    if (pruned.length !== selectedFarms.length) {
      setSelectedFarms(pruned);
      setSelectedBatches([]);
      setPreSelectedBatches([]);
      setTruncatedCount(0);
      setTimeFilter("");
      resetResults();
    }
  };

  const onFilterModeChange = (e) => {
    setFilterMode(e.target.value);
    setSelectedBatches([]);
    setPreSelectedBatches([]);
    setTruncatedCount(0);
    setTimeFilter("");
    resetResults();
  };

  const batchLabel = (batch) =>
    multiFarm && batch.farmName
      ? `${batch.farmName} — ${batch.batchName}`
      : batch.batchName;

  const handleBatchSelection = (batch) => {
    if (selectedBatches.some((b) => b.id === batch.batchID)) {
      setSelectedBatches(selectedBatches.filter((b) => b.id !== batch.batchID));
      return;
    }
    if (selectedBatches.length >= maxBatches) {
      alert(`You can select up to ${maxBatches} batches only.`);
      return;
    }
    setSelectedBatches([
      ...selectedBatches,
      { id: batch.batchID, name: batchLabel(batch) },
    ]);
  };

  const removeBatch = (batchID) => {
    setSelectedBatches(selectedBatches.filter((b) => b.id !== batchID));
  };

  const computeRange = (value) => {
    const end = moment().endOf("day");
    const thisMonthStart = end.clone().startOf("month");

    let start;
    if (value === "3months") {
      start = thisMonthStart.clone().subtract(2, "months");
    } else if (value === "6months") {
      start = thisMonthStart.clone().subtract(5, "months");
    } else if (value === "12months") {
      start = thisMonthStart.clone().subtract(11, "months");
    } else {
      return { startDate: "", endDate: "" };
    }

    return {
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
    };
  };

  const onTimeFilterChange = async (e) => {
    const value = e.target.value;
    setTimeFilter(value);
    resetResults();

    const { startDate, endDate } = computeRange(value);
    if (!value || !farmKey) {
      setPreSelectedBatches([]);
      setTruncatedCount(0);
      return;
    }

    try {
      const response = await axiosInstance.getBatchesByTimeInterval(
        startDate,
        endDate,
        farmKey
      );
      const batches = response.list || [];
      // Endpoint returns newest first; keep only what the comparison allows
      setPreSelectedBatches(batches.slice(0, maxBatches));
      setTruncatedCount(Math.max(0, batches.length - maxBatches));
    } catch (err) {
      console.log("Error fetching batches by time interval:", err.message);
      setPreSelectedBatches([]);
      setTruncatedCount(0);
    }
  };

  const isFormValid = () => {
    if (selectedFarms.length === 0 || !filterMode) return false;
    if (filterMode === "batch") return selectedBatches.length > 0;
    if (filterMode === "time") return timeFilter !== "" && preSelectedBatches.length > 0;
    return false;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const batchIDs =
      filterMode === "time"
        ? preSelectedBatches.map((b) => b.batchID)
        : selectedBatches.map((b) => b.id);

    if (batchIDs.length === 0) {
      alert(`Please select 1 to ${maxBatches} batches.`);
      return;
    }

    setIsLoadingChart(true);
    resetResults();

    try {
      const fetcher = fetchData || axiosInstance.batchComparison_values;
      const response = await fetcher({
        filterMode,
        batchIDs,
        farmIDs: selectedFarms.map((f) => f.farmID),
        timeFilter,
      });

      if (response.list && response.list.length > 0) {
        setChartData(response.list);
        setShowChart(true);
      } else {
        alert(
          t('analytics:compare.noBatchData')
        );
        resetResults();
      }
    } catch (err) {
      console.error("Error fetching chart data:", err);
      if (err.response && err.response.status >= 500) {
        alert(t('analytics:compare.serverError'));
      } else {
        alert(
          t('analytics:compare.loadFailed')
        );
      }
      resetResults();
    } finally {
      setIsLoadingChart(false);
    }
  };

  const renderBatchDisplay = () => {
    if (selectedBatches.length === 0) {
      return <span className="batch-placeholder">{t('analytics:heat.batchesPlaceholder')}</span>;
    }
    if (selectedBatches.length <= 2) {
      return selectedBatches.map((batch) => (
        <span key={batch.id} className="batch-tag">
          {batch.name}
          <button
            type="button"
            className="remove-btn"
            onClick={(e) => {
              e.stopPropagation();
              removeBatch(batch.id);
            }}
          >
            ×
          </button>
        </span>
      ));
    }
    return (
      <span className="batch-count">
        {selectedBatches.length} batches selected
      </span>
    );
  };

  return (
    <div className="batchOperations">
      <div className="row mb-3">
        <div className="col-sm-12">
          <h4 className="m-0 text-dark">{title}</h4>
        </div>
      </div>

      <div className="batch-comparison-container">
        <div className="row">
          {/* Filters Sidebar */}
          <div className="col-md-3">
            <div className="filters-sidebar">
              <div className="sidebar-title">{t('common:actions.applyFilters')}</div>

              <form onSubmit={onSubmit}>
                {role === Role.Admin && (
                  <div className="filter-section">
                    <h6>{t('common:select.farmer')}</h6>
                    <div className="form-group">
                      <Multiselect
                        options={farmerOptions}
                        selectedValues={selectedFarmers}
                        onSelect={onFarmerSelect}
                        onRemove={onFarmerSelect}
                        displayValue="name"
                        showCheckbox
                        placeholder={t('analytics:compare.allFarmers')}
                        avoidHighlightFirstOption
                      />
                    </div>
                  </div>
                )}

                <div className="filter-section">
                  <h6>{t('analytics:compare.selectBarns')}</h6>
                  <div className="form-group">
                    <Multiselect
                      key={farmerKey}
                      options={visibleFarmOptions}
                      selectedValues={selectedFarms}
                      onSelect={onFarmSelect}
                      onRemove={onFarmSelect}
                      displayValue="name"
                      groupBy={role === Role.Admin ? "farmerName" : undefined}
                      showCheckbox={role === Role.Admin}
                      placeholder={t('analytics:compare.selectBarnsPlaceholder')}
                      avoidHighlightFirstOption
                    />
                  </div>
                </div>

                <div className="filter-section">
                  <h6>{t('common:fields.filterMode')}</h6>
                  <div className="form-group">
                    <select
                      className="form-control form-control-sm"
                      value={filterMode}
                      onChange={onFilterModeChange}
                    >
                      <option value="">-Select Mode-</option>
                      <option value="batch">{t('common:select.batches')}</option>
                      <option value="time">{t('common:fields.timeInterval')}</option>
                    </select>
                  </div>
                </div>

                {filterMode === "batch" && (
                  <div className="filter-section">
                    <h6>{t('common:select.batches')}</h6>
                    <div className="form-group">
                      <label>{t('analytics:compare.chooseUpTo')} {maxBatches} batches</label>
                      <div className="batch-multi-select" ref={dropdownRef}>
                        <div
                          className={`batch-display ${
                            showBatchDropdown ? "open" : ""
                          }`}
                          onClick={() => setShowBatchDropdown(!showBatchDropdown)}
                        >
                          {renderBatchDisplay()}
                        </div>

                        {showBatchDropdown && (
                          <div className="batch-dropdown">
                            <div className="dropdown-header">
                              Select batches ({selectedBatches.length}/
                              {maxBatches})
                            </div>
                            {isLoadingBatches ? (
                              <div className="no-batches">{t('analytics:compare.loadingBatches')}</div>
                            ) : batchData.length === 0 ? (
                              <div className="no-batches">
                                {t('analytics:compare.noBatchesSelectBarn')}
                              </div>
                            ) : (
                              batchData.map((item) => (
                                <div
                                  key={item.batchID}
                                  className="batch-option"
                                  onClick={() => handleBatchSelection(item)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedBatches.some(
                                      (batch) => batch.id === item.batchID
                                    )}
                                    onChange={() => {}}
                                  />
                                  <label>{batchLabel(item)}</label>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {filterMode === "time" && (
                  <div className="filter-section">
                    <h6>{t('common:fields.timeInterval')}</h6>
                    <div className="form-group">
                      <label>{t('common:select.timeRange')}</label>
                      <select
                        className="form-control"
                        value={timeFilter}
                        onChange={onTimeFilterChange}
                      >
                        <option value="">-Select Interval-</option>
                        <option value="3months">{t('analytics:heat.last3Months')}</option>
                        <option value="6months">{t('analytics:heat.last6Months')}</option>
                        <option value="12months">{t('analytics:heat.last1Year')}</option>
                      </select>
                    </div>

                    {truncatedCount > 0 && (
                      <div
                        className="pre-selected-batches"
                        style={{ color: "#856404", fontSize: "12px" }}
                      >
                        {t('analytics:compare.showingMostRecent', { shown: maxBatches, total: preSelectedBatches.length + truncatedCount })}
                      </div>
                    )}

                    {preSelectedBatches.length > 0 && (
                      <div className="pre-selected-batches">
                        <h6>{t('common:fields.foundBatches')}{preSelectedBatches.length})</h6>
                        <div className="pre-selected-batch-list">
                          {preSelectedBatches.map((batch) => (
                            <span
                              key={batch.batchID}
                              className="pre-selected-batch-item"
                            >
                              {batchLabel(batch)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {timeFilter &&
                      preSelectedBatches.length === 0 &&
                      farmKey && (
                        <div
                          className="pre-selected-batches"
                          style={{ textAlign: "center" }}
                        >
                          <div style={{ color: "#6c757d", fontSize: "12px" }}>
                            {t('analytics:compare.noBatchesInterval')}
                          </div>
                        </div>
                      )}
                  </div>
                )}

                {filterMode && (
                  <div className="generate-btn-section">
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={!isFormValid()}
                    >
                      {t('common:actions.generateReport')}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Results Area */}
          <div className="col-md-9">
            <div className="card">
              <div className="card-body">
                {showChart ? (
                  renderChart(chartData, {
                    mode: filterMode,
                    isLoading: isLoadingChart,
                  })
                ) : (
                  <div className="placeholder-message">
                    <div className="placeholder-icon">
                      <PieChartOutlined
                        style={{ fontSize: 40, color: "#4e4ead" }}
                      />
                    </div>
                    <div className="placeholder-title">{t('analytics:compare.readyForAnalysis')}</div>
                    <div className="placeholder-text">
                      {t('analytics:compare.placeholderText')}
                    </div>
                    <div className="placeholder-steps">
                      <h6>{t('analytics:heat.quickStart')}</h6>
                      <ol>
                        {role === Role.Admin && (
                          <li>{t('analytics:compare.stepFarmersOptional')}</li>
                        )}
                        <li>{t('analytics:compare.stepBarns')}</li>
                        <li>{t('analytics:heat.stepFilterMode')}</li>
                        <li>{t('analytics:heat.stepCriteria')}</li>
                        <li>{t('analytics:compare.stepGenerate')}</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonReport;
